import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../../services/api";

// Only Carpenters self-register. Labour is added by Carpenter from their dashboard.

export default function Register() {
  const navigate = useNavigate();

  const [form, setForm]       = useState({ name: "", email: "", password: "", role: "carpenter" }); // role is always carpenter
  const [error, setError]     = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [focused, setFocused] = useState("");
  const [showPass, setShowPass] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/register", form);
      setSuccess(true);
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      setError(err.response?.data?.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        @keyframes spin    { to { transform: rotate(360deg); } }
        @keyframes slideUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
        @keyframes popIn   { from { opacity:0; transform:scale(0.82); } to { opacity:1; transform:scale(1); } }

        *, *::before, *::after { box-sizing: border-box; margin:0; padding:0; }
        body { font-family: 'Plus Jakarta Sans', sans-serif; }

        input::placeholder { color: #A8A29E; }
        input:-webkit-autofill {
          -webkit-box-shadow: 0 0 0 100px #FFF7ED inset !important;
          -webkit-text-fill-color: #1C1917 !important;
        }

        .reg-submit:hover:not(:disabled) {
          background: #B45309 !important;
          transform: translateY(-1px);
          box-shadow: 0 8px 24px rgba(180,83,9,0.3) !important;
        }
        .reg-submit:active:not(:disabled) { transform: translateY(0) !important; }

        .role-opt:hover {
          border-color: #D97706 !important;
          background: #FFFBF7 !important;
        }

        /* ── RESPONSIVE ── */

        /* Left panel: desktop only */
        .reg-panel {
          display: flex;
          width: 400px;
          min-height: 100vh;
          flex-shrink: 0;
          background: linear-gradient(155deg, #92400E 0%, #B45309 45%, #D97706 100%);
          align-items: center;
          justify-content: center;
          padding: 48px 36px;
          position: relative;
          overflow: hidden;
        }

        @media (max-width: 768px) {
          .reg-panel        { display: none !important; }
          .reg-root         { flex-direction: column !important; }
          .reg-form-panel   { padding: 0 !important; align-items: stretch !important; }
          .reg-card         { max-width: 100% !important; border-radius: 0 !important; min-height: 100vh; padding: 0 !important; }
          .reg-mobile-top   { display: flex !important; }
          .reg-form-body    { padding: 24px 18px 48px !important; }
          .reg-desktop-logo { display: none !important; }
          /* Stack role cards vertically on very small screens */
          .role-grid        { grid-template-columns: 1fr 1fr; }
        }

        @media (max-width: 380px) {
          .role-grid { grid-template-columns: 1fr !important; }
        }

        @media (min-width: 769px) and (max-width: 1024px) {
          .reg-panel { width: 310px !important; padding: 40px 26px !important; }
        }

        /* Desktop: hide mobile top bar */
        .reg-mobile-top { display: none; }
      `}</style>

      <div className="reg-root" style={root}>

        {/* ── LEFT PANEL (desktop) ── */}
        <div className="reg-panel">
          <div style={{ position: "relative", zIndex: 2, color: "#FFFBF7" }}>
            <div style={panelLogo}><ToolIcon size={34} color="#FFFBF7" /></div>
            <h1 style={panelTitle}>Join Craftigo</h1>
            <p style={panelSub}>Connect with clients, manage projects, and grow your craft business.</p>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {[
                { icon: "📋", text: "Track projects easily" },
                { icon: "💰", text: "Get paid on time" },
                { icon: "⭐", text: "Build your reputation" },
                { icon: "📱", text: "Works on all devices" },
              ].map(f => (
                <div key={f.text} style={featureItem}>
                  <span style={{ fontSize: "17px" }}>{f.icon}</span>
                  <span style={{ fontSize: "13px", fontWeight: "500", opacity: 0.92 }}>{f.text}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={grainOverlay} />
        </div>

        {/* ── RIGHT FORM PANEL ── */}
        <div className="reg-form-panel" style={formPanel}>
          <div className="reg-card" style={formCard}>

            {/* MOBILE TOP BAR */}
            <div className="reg-mobile-top" style={mobileTop}>
              <ToolIcon size={22} color="#FFFBF7" />
              <span style={{ fontWeight: "800", fontSize: "17px", color: "#FFFBF7", letterSpacing: "-0.3px" }}>
                Craftigo
              </span>
            </div>

            {/* FORM BODY */}
            <div className="reg-form-body" style={formBody}>

              {/* Desktop logo */}
              <div className="reg-desktop-logo" style={desktopLogoRow}>
                <ToolIcon size={18} color="#D97706" />
                <span style={{ fontWeight: "800", fontSize: "15px", color: "#1C1917" }}>Craftigo</span>
              </div>

              {success ? (
                <div style={successBox}>
                  <div style={checkRing}>
                    <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
                      <polyline points="20 6 9 17 4 12" stroke="#D97706" strokeWidth="2.5"
                        strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <h2 style={{ ...formTitle, textAlign: "center" }}>Account Created! 🎉</h2>
                  <p style={{ fontSize: "15px", color: "#78716C", textAlign: "center" }}>
                    Taking you to sign in…
                  </p>
                </div>
              ) : (
                <>
                  <h2 style={formTitle}>Create your account</h2>
                  <p style={formSub}>Start managing your craft work today</p>

                  <form onSubmit={handleSubmit} noValidate style={formStyle}>

                    {/* Full Name */}
                    <div style={fieldWrap}>
                      <label style={labelSt} htmlFor="name">Full name</label>
                      <div style={inputWrap}>
                        <span style={iconLeft}>
                          <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                              stroke={focused === "name" ? "#D97706" : "#A8A29E"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            <circle cx="12" cy="7" r="4"
                              stroke={focused === "name" ? "#D97706" : "#A8A29E"} strokeWidth="1.8" />
                          </svg>
                        </span>
                        <input
                          id="name" name="name" type="text"
                          placeholder="Ramesh Kumar"
                          value={form.name} onChange={handleChange} required
                          style={focused === "name" ? { ...inputSt, ...inputFocus } : inputSt}
                          onFocus={() => setFocused("name")}
                          onBlur={() => setFocused("")}
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div style={fieldWrap}>
                      <label style={labelSt} htmlFor="email">Email address</label>
                      <div style={inputWrap}>
                        <span style={iconLeft}>
                          <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                              stroke={focused === "email" ? "#D97706" : "#A8A29E"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            <polyline points="22,6 12,13 2,6"
                              stroke={focused === "email" ? "#D97706" : "#A8A29E"} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                        <input
                          id="email" name="email" type="email"
                          placeholder="you@example.com"
                          value={form.email} onChange={handleChange} required
                          style={focused === "email" ? { ...inputSt, ...inputFocus } : inputSt}
                          onFocus={() => setFocused("email")}
                          onBlur={() => setFocused("")}
                        />
                      </div>
                    </div>

                    {/* Password */}
                    <div style={fieldWrap}>
                      <label style={labelSt} htmlFor="password">Password</label>
                      <div style={inputWrap}>
                        <span style={iconLeft}>
                          <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                            <rect x="3" y="11" width="18" height="11" rx="2"
                              stroke={focused === "password" ? "#D97706" : "#A8A29E"} strokeWidth="1.8" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4"
                              stroke={focused === "password" ? "#D97706" : "#A8A29E"} strokeWidth="1.8" strokeLinecap="round" />
                          </svg>
                        </span>
                        <input
                          id="password" name="password"
                          type={showPass ? "text" : "password"}
                          placeholder="Min. 8 characters"
                          value={form.password} onChange={handleChange} required minLength={8}
                          style={focused === "password" ? { ...inputSt, ...inputFocus } : inputSt}
                          onFocus={() => setFocused("password")}
                          onBlur={() => setFocused("")}
                        />
                        <button type="button" onClick={() => setShowPass(!showPass)} style={eyeBtn} aria-label="Toggle password">
                          {showPass
                            ? <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" stroke="#A8A29E" strokeWidth="1.8" strokeLinecap="round" />
                                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" stroke="#A8A29E" strokeWidth="1.8" strokeLinecap="round" />
                                <line x1="1" y1="1" x2="23" y2="23" stroke="#A8A29E" strokeWidth="1.8" strokeLinecap="round" />
                              </svg>
                            : <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="#A8A29E" strokeWidth="1.8" />
                                <circle cx="12" cy="12" r="3" stroke="#A8A29E" strokeWidth="1.8" />
                              </svg>
                          }
                        </button>
                      </div>
                    </div>

                    {/* Carpenter-only info banner */}
                    <div style={infoBanner}>
                      <div style={infoBannerLeft}>
                        <span style={infoBannerIcon}>🪚</span>
                        <div>
                          <p style={infoBannerTitle}>Carpenter Registration</p>
                          <p style={infoBannerSub}>This page is for Carpenters only</p>
                        </div>
                      </div>
                      <span style={infoBadge}>✓ Verified Role</span>
                    </div>

                    {/* Labour info note */}
                    <div style={labourNote}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, marginTop: "1px" }}>
                        <circle cx="12" cy="12" r="10" stroke="#92400E" strokeWidth="2"/>
                        <line x1="12" y1="8" x2="12" y2="12" stroke="#92400E" strokeWidth="2" strokeLinecap="round"/>
                        <circle cx="12" cy="16" r="1" fill="#92400E"/>
                      </svg>
                      <span>
                        <strong>Are you a Labour?</strong> Your account will be created by your Carpenter from their dashboard.
                      </span>
                    </div>

                    {/* Error */}
                    {error && (
                      <div style={errorBox} role="alert">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                          <circle cx="12" cy="12" r="10" stroke="#DC2626" strokeWidth="2" />
                          <line x1="15" y1="9" x2="9" y2="15" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" />
                          <line x1="9" y1="9" x2="15" y2="15" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                        {error}
                      </div>
                    )}

                    {/* Submit */}
                    <button
                      type="submit" disabled={loading}
                      className="reg-submit"
                      style={{ ...btnSt, ...(loading ? { opacity: 0.7, cursor: "not-allowed" } : {}) }}
                    >
                      {loading
                        ? <span style={flexCenter}>
                            <span style={spinner} /> Creating account…
                          </span>
                        : "Create Account"
                      }
                    </button>
                  </form>

                  <p style={footerTxt}>
                    Already have an account?{" "}
                    <Link to="/login" style={linkSt}>Sign in →</Link>
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function ToolIcon({ size = 24, color = "#D97706" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"
        stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      />
    </svg>
  );
}

/* ── Styles ── */
const root = {
  minHeight: "100vh",
  display: "flex",
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  backgroundColor: "#FFFBF7",
};
const panelLogo = {
  width: "60px", height: "60px",
  background: "rgba(255,255,255,0.15)",
  borderRadius: "16px",
  display: "flex", alignItems: "center", justifyContent: "center",
  marginBottom: "22px",
  backdropFilter: "blur(8px)",
  border: "1px solid rgba(255,255,255,0.2)",
};
const panelTitle = { fontSize: "28px", fontWeight: "800", color: "#FFFBF7", marginBottom: "10px", letterSpacing: "-0.5px" };
const panelSub   = { fontSize: "14px", lineHeight: "1.65", color: "#FFFBF7", opacity: 0.85, marginBottom: "28px", maxWidth: "270px" };
const featureItem = {
  display: "flex", alignItems: "center", gap: "12px",
  padding: "10px 14px",
  background: "rgba(255,255,255,0.12)",
  borderRadius: "10px",
  backdropFilter: "blur(4px)",
  border: "1px solid rgba(255,255,255,0.15)",
  color: "#FFFBF7",
};
const grainOverlay = {
  position: "absolute", inset: 0, zIndex: 1, opacity: 0.4, pointerEvents: "none",
  background: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.08'/%3E%3C/svg%3E\")",
};
const formPanel = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#FFFBF7",
  padding: "32px 20px",
  overflowY: "auto",
};
const formCard = {
  width: "100%",
  maxWidth: "440px",
  animation: "slideUp 0.4s ease both",
};
const mobileTop = {
  alignItems: "center",
  gap: "10px",
  padding: "18px 20px",
  background: "linear-gradient(135deg, #92400E, #D97706)",
};
const desktopLogoRow = { display: "flex", alignItems: "center", gap: "8px", marginBottom: "26px" };
const formBody = { padding: "36px 32px 44px" };
const formTitle = { fontSize: "25px", fontWeight: "800", color: "#1C1917", marginBottom: "6px", letterSpacing: "-0.5px" };
const formSub   = { fontSize: "14px", color: "#78716C", marginBottom: "26px" };
const formStyle = { display: "flex", flexDirection: "column", gap: "18px" };
const fieldWrap = { display: "flex", flexDirection: "column", gap: "6px" };
const labelSt   = { fontSize: "13px", fontWeight: "600", color: "#44403C" };
const inputWrap = { position: "relative", display: "flex", alignItems: "center" };
const iconLeft  = { position: "absolute", left: "14px", display: "flex", alignItems: "center", pointerEvents: "none" };
const inputSt = {
  width: "100%",
  padding: "14px 46px",
  backgroundColor: "#FFF7ED",
  border: "1.5px solid #E7E5E4",
  borderRadius: "12px",
  color: "#1C1917",
  fontSize: "15px",
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  outline: "none",
  transition: "border-color 0.2s, box-shadow 0.2s, background 0.2s",
  WebkitAppearance: "none",
};
const inputFocus = {
  border: "1.5px solid #D97706",
  backgroundColor: "#FFFBF7",
  boxShadow: "0 0 0 3px rgba(217,119,6,0.13)",
};
const eyeBtn = {
  position: "absolute", right: "10px",
  background: "none", border: "none", cursor: "pointer",
  padding: "6px", display: "flex", alignItems: "center",
  minWidth: "44px", justifyContent: "center",
};
const roleGrid = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" };
const roleCardBase = {
  position: "relative",
  display: "flex", flexDirection: "column", alignItems: "center",
  gap: "6px", padding: "18px 10px",
  backgroundColor: "#FFF7ED",
  border: "1.5px solid #E7E5E4",
  borderRadius: "14px",
  cursor: "pointer",
  transition: "border-color 0.2s, background 0.2s, box-shadow 0.2s",
  textAlign: "center",
  minHeight: "100px",
};
const roleCardActive = {
  ...roleCardBase,
  border: "1.5px solid #D97706",
  backgroundColor: "#FFFBF7",
  boxShadow: "0 0 0 3px rgba(217,119,6,0.13)",
};
const roleLabel = { fontSize: "14px", fontWeight: "700", color: "#1C1917" };
const roleDesc  = { fontSize: "11px", color: "#78716C", lineHeight: "1.4" };
const checkBadge = {
  position: "absolute", top: "10px", right: "10px",
  width: "19px", height: "19px",
  background: "#D97706", borderRadius: "50%",
  display: "flex", alignItems: "center", justifyContent: "center",
};
const errorBox = {
  display: "flex", alignItems: "center", gap: "9px",
  padding: "12px 14px",
  background: "#FEF2F2",
  border: "1px solid #FECACA",
  borderRadius: "10px",
  color: "#DC2626", fontSize: "13px", fontWeight: "500",
};
const btnSt = {
  padding: "15px",
  background: "#D97706", color: "#FFFBF7",
  border: "none", borderRadius: "12px",
  fontSize: "16px", fontWeight: "700",
  cursor: "pointer",
  transition: "background 0.18s, transform 0.15s, box-shadow 0.18s",
  boxShadow: "0 4px 14px rgba(217,119,6,0.24)",
  letterSpacing: "0.1px",
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  width: "100%",
};
const flexCenter = { display: "flex", alignItems: "center", justifyContent: "center", gap: "9px" };
const spinner = {
  display: "inline-block", width: "16px", height: "16px",
  border: "2px solid rgba(255,251,247,0.3)",
  borderTop: "2px solid #FFFBF7",
  borderRadius: "50%",
  animation: "spin 0.7s linear infinite",
};
const footerTxt = { marginTop: "24px", textAlign: "center", fontSize: "14px", color: "#78716C" };
const linkSt    = { color: "#D97706", textDecoration: "none", fontWeight: "700" };
const successBox = {
  display: "flex", flexDirection: "column", alignItems: "center",
  padding: "36px 0 16px", gap: "16px",
  animation: "popIn 0.4s ease both",
};
const checkRing = {
  width: "76px", height: "76px",
  background: "#FFF7ED",
  border: "2px solid #D97706",
  borderRadius: "50%",
  display: "flex", alignItems: "center", justifyContent: "center",
};
const infoBanner = {
  display: "flex", alignItems: "center", justifyContent: "space-between",
  padding: "14px 16px",
  background: "linear-gradient(135deg, #FEF3C7, #FFF7ED)",
  border: "1.5px solid #D97706",
  borderRadius: "14px",
  gap: "12px",
};
const infoBannerLeft = { display: "flex", alignItems: "center", gap: "12px" };
const infoBannerIcon = {
  fontSize: "26px", lineHeight: 1,
  background: "rgba(217,119,6,0.12)",
  padding: "8px", borderRadius: "10px",
};
const infoBannerTitle = { fontSize: "14px", fontWeight: "700", color: "#1C1917", marginBottom: "2px" };
const infoBannerSub   = { fontSize: "12px", color: "#78716C" };
const infoBadge = {
  fontSize: "11px", fontWeight: "700",
  color: "#92400E",
  background: "rgba(146,64,14,0.1)",
  border: "1px solid rgba(146,64,14,0.2)",
  borderRadius: "20px",
  padding: "4px 10px",
  whiteSpace: "nowrap",
};
const labourNote = {
  display: "flex", alignItems: "flex-start", gap: "9px",
  padding: "11px 14px",
  background: "#FFFBEB",
  border: "1px solid #FDE68A",
  borderRadius: "10px",
  color: "#78350F",
  fontSize: "12.5px",
  lineHeight: "1.5",
};